// src/utils/fabricSvg.js
//
// Helpers for the SVG that fabric's canvas.toSVG() produces when a shape or
// drawing is inserted into a slide.
//
// fabric serializes at the builder's canvas size — a hardcoded 800x500 — and
// writes that onto the root element as width="800" height="500". Dropped into
// a slide, that is a fixed-size picture that overflows the slide box instead of
// fitting it. The viewBox is already correct, so making the artwork responsive
// is a matter of letting the width follow the container and the height follow
// the aspect ratio.

const SVG_TAG = /<svg\b[^>]*>/i;

/** Does this block's content look like fabric SVG markup rather than HTML? */
export const isSvgMarkup = (content) => {
  if (typeof content !== 'string') return false;
  return SVG_TAG.test(content);
};

const readAttr = (tag, name) => {
  const match = tag.match(new RegExp(`\\b${name}\\s*=\\s*["']([^"']*)["']`, 'i'));
  return match ? match[1].trim() : '';
};

const dropAttr = (tag, name) =>
  tag.replace(new RegExp(`\\s${name}\\s*=\\s*["'][^"']*["']`, 'gi'), '');

/**
 * Rewrite the root <svg> so it scales to whatever box it is placed in while
 * keeping its proportions.
 *
 * Keeps the viewBox (deriving one from width/height when fabric omitted it,
 * which is what preserves the drawing's coordinate space) and removes the fixed
 * pixel width/height that caused the overflow.
 */
export const makeSvgResponsive = (svg) => {
  if (!isSvgMarkup(svg)) return svg;

  return svg.replace(SVG_TAG, (tag) => {
    const width = readAttr(tag, 'width');
    const height = readAttr(tag, 'height');
    let viewBox = readAttr(tag, 'viewBox');

    // No viewBox means the pixel width/height *were* the coordinate space, so
    // rebuild one from them before dropping them.
    if (!viewBox) {
      const w = Number.parseFloat(width);
      const h = Number.parseFloat(height);
      if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
        viewBox = `0 0 ${w} ${h}`;
      }
    }

    let next = dropAttr(tag, 'width');
    next = dropAttr(next, 'height');
    next = dropAttr(next, 'viewBox');
    next = dropAttr(next, 'preserveAspectRatio');
    next = dropAttr(next, 'style');

    const attrs = [
      'width="100%"',
      viewBox ? `viewBox="${viewBox}"` : '',
      'preserveAspectRatio="xMidYMid meet"',
      'style="width:100%;height:auto;max-width:100%;display:block"'
    ].filter(Boolean).join(' ');

    return next.replace(/\s*\/?>$/, ` ${attrs}>`);
  });
};

/**
 * The aspect ratio of the artwork, from its viewBox — used by the slide
 * thumbnail to reserve the right amount of space before the SVG paints.
 * Returns null when it cannot be determined.
 */
export const svgAspectRatio = (svg) => {
  if (!isSvgMarkup(svg)) return null;
  const tag = svg.match(SVG_TAG)?.[0] || '';
  const viewBox = readAttr(tag, 'viewBox');
  const parts = viewBox.split(/[\s,]+/).map(Number);
  if (parts.length === 4 && parts.every(Number.isFinite) && parts[2] > 0 && parts[3] > 0) {
    return parts[2] / parts[3];
  }
  const w = Number.parseFloat(readAttr(tag, 'width'));
  const h = Number.parseFloat(readAttr(tag, 'height'));
  if (Number.isFinite(w) && Number.isFinite(h) && h > 0) return w / h;
  return null;
};
